import { useSelector } from 'react-redux';

import { get } from 'lodash';

import { useCurrentUser } from '@lib/hooks';
import { ProvisioningEnum, RolloutEnum } from '@lib/resources/types';
import { selectCurrentMasterSettings } from '@lib/selectors';

import { useImpersonatedAdminUserId } from 'hooks';
import usePermissions from 'hooks/usePermissions';
import useCurrentMasterReseller from 'hooks/useCurrentMasterReseller';
import useIsLumaAdmin from 'hooks/useIsLumaAdmin';

export default function useControlUserAccess() {
  const { permissions, hasAccessToProduct } = usePermissions();
  const impersonatedAdminUserId = useImpersonatedAdminUserId();

  const currentUser = useCurrentUser();
  const currentMasterSettings = useSelector(selectCurrentMasterSettings);
  const hasCobrandingEnabled = currentMasterSettings.branding?.themes?.find(
    (theme: { scope: string; enabled: string }) => theme?.scope === 'cobranding' && theme?.enabled,
  );
  const hasOutboundNumbersEnbaled = !!currentMasterSettings.communication?.outboundNumbers?.enabled;
  const hasAccessToChecklistTemplate = hasAccessToProduct([RolloutEnum.ChecklistTemplate]);
  const hasAccessToFaxTransform = hasAccessToProduct([RolloutEnum.FaxAutomation]);
  const hasStripeEnabled = !!currentMasterSettings.billing?.vendors?.stripe?.enabled;
  const hasAccountsToImpersonate = currentUser?.impersonate?.length;
  const hasAccessToSpecialty =
    hasAccessToProduct(RolloutEnum.SpecialtyReferrals) && get(currentUser, 'settings.referral.useSpecialty.enabled');
  const hasAccessToEligibilitySetup = hasAccessToProduct([ProvisioningEnum.InsuranceVerification]);

  const isLumaAdmin = useIsLumaAdmin();

  const masterReseller = useCurrentMasterReseller();

  const isAdmin = get(permissions, 'isAdmin');
  const isLumaUser = get(permissions, 'isLumaUser');
  const isManager = get(permissions, 'isManager');
  const isSubaccount = get(permissions, 'isSubaccount');
  const isMasterAccount = get(permissions, 'isMaster');

  return {
    home: {
      myAccount: {
        canView: isAdmin || isManager || isSubaccount,
        profile: {
          canEdit: isAdmin || isManager || isSubaccount,
          canView: isAdmin || isManager || isSubaccount,
        },
        accountDetails: {
          canView: isAdmin || isManager || isSubaccount,
        },
        security: {
          canEdit: isAdmin,
          canView: isAdmin || isManager || isSubaccount,
        },
        preferences: {
          canEdit: isAdmin || isManager || isSubaccount,
          canView: isAdmin || isManager || isSubaccount,
        },
      },
      mainAccount: {
        canView: (isAdmin || isManager) && !hasCobrandingEnabled,
        profile: {
          canView: isAdmin || isManager,
        },
        preferences: {
          canView: isAdmin || isManager,
        },
        licensing: {
          canView: isAdmin || isManager,
        },
        legalAndCompliance: {
          canView: isAdmin || isManager,
        },
        billing: {
          canView: isAdmin || isManager,
        },
      },
    },
    securityCenter: {
      canView: isAdmin || isManager,
      groups: {
        canView: isAdmin || isManager,
        canCreate: isAdmin,
        canEdit: isAdmin,
        canDelete: isAdmin,
      },
      multipleAccountManagement: {
        canView: (isAdmin || isManager) && hasAccountsToImpersonate,
      },
      users: {
        canView: isAdmin || isManager,
        canCreate: isAdmin,
        canEdit: isAdmin,
        canDelete: isAdmin,
        canAddToGroups: isAdmin,
        canAddToRoles: isAdmin,
        canResetPassword: isAdmin,
        canSuspendUser: isAdmin,
        canUploadUsers: isAdmin,
        canViewInvite: isAdmin || isManager,
      },
      tokens: {
        canView: isAdmin,
      },
    },
    practiceResources: {
      canView: isAdmin || isManager,
      apptType: {
        canView: isAdmin || isManager,
        canEdit: isAdmin,
        canEditSettings: isAdmin,
      },
      facilities: {
        canView: isAdmin || isManager,
        canEdit: isAdmin,
        canEditSettings: isAdmin,
      },
      providers: {
        canView: isAdmin || isManager,
        canEdit: isAdmin,
        canEditSettings: isAdmin,
      },
      specialties: {
        canView: (isAdmin || isManager) && hasAccessToSpecialty,
      },
    },
    featureSettings: {
      canView: isAdmin || isManager,
      canEditAllSettings: isAdmin,
    },
    utilities: {
      canView: isAdmin || isManager,
      branding: {
        canView: isAdmin || isManager,
        canEdit: isAdmin,
        cobranding: {
          canView: isAdmin,
          canEdit: isAdmin && (!!impersonatedAdminUserId || !!isLumaUser),
        },
      },
      checklistBuilder: {
        canView: hasAccessToChecklistTemplate && (isAdmin || !!impersonatedAdminUserId || !!isLumaUser),
        canEdit: hasAccessToChecklistTemplate && (isAdmin || !!impersonatedAdminUserId || !!isLumaUser),
        canCreate: hasAccessToChecklistTemplate && (isAdmin || !!impersonatedAdminUserId || !!isLumaUser),
      },
      faxTransformAutomations: {
        canView: hasAccessToFaxTransform && (isAdmin || !!impersonatedAdminUserId || !!isLumaUser),
        canEdit: hasAccessToFaxTransform && (isAdmin || !!impersonatedAdminUserId || !!isLumaUser),
        canCreate: hasAccessToFaxTransform && (isAdmin || !!impersonatedAdminUserId || !!isLumaUser),
      },
      eligibilitySetup: {
        payerManagement: {
          canView: hasAccessToEligibilitySetup && (!!impersonatedAdminUserId || !!isLumaUser || !!isAdmin),
        },
        canView: hasAccessToEligibilitySetup && (!!impersonatedAdminUserId || !!isLumaUser || !!isAdmin),
      },
      thirdPartyIntegrations: {
        twilioNumberManagement: {
          canView: !!isAdmin,
          canEdit: !!isAdmin,
        },
        ehrIntegrators: {
          canView: !!(isAdmin || isLumaAdmin || impersonatedAdminUserId) && !!masterReseller,
          canEdit: !!(isAdmin || isLumaAdmin || impersonatedAdminUserId) && !!masterReseller,
        },
      },
      omnichannelRetries: {
        canView: isAdmin || isManager,
        canEdit: isAdmin,
      },
      lumaPay: {
        canView: isAdmin && hasStripeEnabled,
        canEdit: isAdmin && hasStripeEnabled,
      },
      messagePartials: {
        canView: isAdmin,
        canEdit: isAdmin,
      },
      followupWorkflows: {
        canView: true,
      },
      customActions: {
        canCreate: isAdmin,
        canView: isAdmin || isManager,
        canEdit: isAdmin,
      },
      formBuilder: {
        canCreate: isAdmin || isManager,
        canEdit: isAdmin || isManager,
        canView: isAdmin || isManager,
      },
      messageLibrary: {
        canCreate: isAdmin || isManager,
        canEdit: isAdmin || isManager,
        canView: isAdmin || isManager,
      },
      messageTemplates: {
        canCreate: isAdmin,
        canEdit: isAdmin,
        canView: isAdmin || isManager,
      },
      patientBilling: {
        canView: isAdmin || isManager,
        canEdit: isAdmin,
      },
      designatedNumbers: {
        canView: hasOutboundNumbersEnbaled,
        canEdit: isMasterAccount,
      },
      assistants: {
        canCreate: isAdmin || isManager,
        canEdit: isAdmin || isManager,
        canView: isAdmin || isManager,
      },
    },
  };
}
